/**
 * <amq-haertegrad-rechner>: the firmness calculator of the product page
 * (blocks/amq_product_haertegrad_rechner.liquid). The customer enters height and weight, picks a
 * sleeping position and gets the recommended Härtegrad for this product; "Härtegrad auswählen"
 * selects that value in the variant picker.
 *
 * The calculation is a 1:1 port of the WordPress plugin (amq-haertegrad-rechner.js): each matrix
 * row is the lower boundary line of a band, stored as the weight at the smallest height
 * (gewicht_links) and at the largest height (gewicht_rechts). Every boundary is interpolated to
 * the entered height; the body sits in the highest band whose boundary its weight clears. A band
 * whose grade carries a slash ("H4/H5") is a transition: the harder grade is recommended and the
 * softer one mentioned. A body below every boundary gets no recommendation.
 *
 * The sleeping position is a marked choice only, as on the live store: rows without a position
 * apply to everyone and are what every matrix carries today (see pickRows).
 *
 * Opened by any link to `#haertegrad-rechner` on the page (the help badge in the picker fork);
 * the dialog itself is Horizon's dialog-component.
 */

const OPENER_SELECTOR = 'a[href$="#haertegrad-rechner"]';
const SPINNER_MS = 650; // short pause so the result reads as calculated, like live

/**
 * Numeric order of a band label, so equal boundary lines resolve to the right band: a pure grade
 * ranks on its number ("H4" → 4), a transition between the two ("H4/H5" → 4.5).
 * @param {string} grade
 * @returns {number}
 */
export function gradeOrder(grade) {
  const match = String(grade).match(/(\d)(?:\/H?(\d))?/i);
  if (!match) return 0;
  const first = Number.parseInt(match[1] ?? '', 10);
  const second = match[2] ? Number.parseInt(match[2], 10) : first;
  return (first + second) / 2;
}

/**
 * Rows the calculation runs on: those without a sleeping position; if a matrix only has
 * position-specific rows, all of them (same as the plugin).
 * @param {MatrixRow[]} matrix
 * @returns {MatrixRow[]}
 */
export function pickRows(matrix) {
  const neutral = matrix.filter((row) => !row.schlafposition);
  return neutral.length ? neutral : matrix;
}

/**
 * @typedef {object} MatrixRow
 * @property {string} haertegrad - "H3" or a transition "H3/H4"
 * @property {string} [schlafposition]
 * @property {number} gewicht_links - boundary weight at groesse_min
 * @property {number} gewicht_rechts - boundary weight at groesse_max
 * @property {number} groesse_min
 * @property {number} groesse_max
 * @property {number} gewicht_axis_min
 * @property {number} gewicht_axis_max
 */

/**
 * @typedef {object} Recommendation
 * @property {'found' | 'transition' | 'not-found'} state
 * @property {string} [grade] - recommended grade, e.g. "H3"
 * @property {string} [softerGrade] - transition only
 * @property {number | null} [weightMin] - found only: rounded lower boundary of the band
 * @property {number | null} [weightMax] - found only: rounded lower boundary of the next band
 * @property {boolean} [lowestBand] - found only: no lower limit is communicated
 */

/**
 * @param {MatrixRow[]} rows
 * @param {number} height
 * @param {number} weight
 * @returns {Recommendation}
 */
export function calculate(rows, height, weight) {
  if (!rows.length) return { state: 'not-found' };

  const bands = rows.map((row) => {
    const span = row.groesse_max - row.groesse_min;
    let t = span > 0 ? (height - row.groesse_min) / span : 0;
    if (t < 0) t = 0;
    else if (t > 1) t = 1;
    return {
      grade: row.haertegrad,
      order: gradeOrder(row.haertegrad),
      boundary: row.gewicht_links + (row.gewicht_rechts - row.gewicht_links) * t,
    };
  });

  // Ascending by lower boundary; equal lines resolve by grade order, so a zero-width transition
  // never masks the pure band above it.
  bands.sort((a, b) => a.boundary - b.boundary || a.order - b.order);

  let index = -1;
  for (let i = 0; i < bands.length; i++) {
    const band = bands[i];
    if (band && weight >= band.boundary) index = i;
    else break;
  }
  const band = bands[index];
  if (!band) return { state: 'not-found' };

  if (band.grade.includes('/')) {
    const [first = '', second = ''] = band.grade.split('/');
    const a = Number.parseInt(first.replace(/[^0-9]/g, ''), 10);
    const b = Number.parseInt(second.replace(/[^0-9]/g, ''), 10);
    return { state: 'transition', grade: `H${Math.max(a, b)}`, softerGrade: `H${Math.min(a, b)}` };
  }

  const upper = bands[index + 1];
  return {
    state: 'found',
    grade: band.grade,
    weightMin: Math.round(band.boundary),
    weightMax: upper ? Math.round(upper.boundary) : null,
    lowestBand: index === 0,
  };
}

/**
 * Sentences for a recommendation. Templates use printf-style placeholders (%s, %1$s, %2$s), filled
 * from the theme's translations by the block.
 * @param {Recommendation} result
 * @param {Record<string, string>} texts
 * @returns {{ name: string, range: string }}
 */
export function describe(result, texts) {
  const fill = (/** @type {string | undefined} */ template, /** @type {(string | number)[]} */ ...values) =>
    (template ?? '')
      .replace('%1$s', String(values[0] ?? ''))
      .replace('%2$s', String(values[1] ?? ''))
      .replace('%s', String(values[0] ?? ''));

  const grade = result.grade ?? '';
  const name = fill(texts.gradeName, grade.replace(/\D/g, ''));

  if (result.state === 'transition') {
    return { name, range: fill(texts.transitionHint, grade, result.softerGrade ?? '') };
  }

  let range;
  if (result.weightMax == null) range = fill(texts.rangeFrom, result.weightMin ?? '');
  else if (result.lowestBand) range = fill(texts.rangeTo, result.weightMax);
  else range = fill(texts.rangeBetween, result.weightMin ?? '', result.weightMax);

  return { name, range: fill(texts.foundRange, grade, range) };
}

if (typeof HTMLElement !== 'undefined' && !customElements.get('amq-haertegrad-rechner')) {
  customElements.define(
    'amq-haertegrad-rechner',
    class AmqHaertegradRechner extends HTMLElement {
      /** @type {MatrixRow[]} */
      #rows = [];

      /** @type {Record<string, string>} */
      #texts = {};

      /** @type {string | null} */
      #grade = null;

      /** Product id the current rows belong to ('' = the block's own rows) */
      #rowsProductId = '';

      /** @type {ReturnType<typeof setTimeout> | undefined} */
      #timer;

      connectedCallback() {
        const data = this.#readData();
        if (!data) return;

        this.#rows = pickRows(data.rows);
        this.#texts = data.texts;
        this.#setBounds();

        this.addEventListener('click', this.#onClick);
        this.addEventListener('input', this.#onInput);
        this.addEventListener('submit', this.#onSubmit);
        document.addEventListener('click', this.#onDocumentClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.#onClick);
        this.removeEventListener('input', this.#onInput);
        this.removeEventListener('submit', this.#onSubmit);
        document.removeEventListener('click', this.#onDocumentClick);
        clearTimeout(this.#timer);
      }

      /** @returns {{ rows: MatrixRow[], texts: Record<string, string> } | null} */
      #readData() {
        const script = this.querySelector('script[type="application/json"]');
        try {
          const data = JSON.parse(script?.textContent ?? '');
          if (!Array.isArray(data.rows) || !data.rows.length) return null;
          return { rows: data.rows, texts: data.texts ?? {} };
        } catch (error) {
          console.warn('[amq-haertegrad-rechner] matrix data is not valid JSON', error);
          return null;
        }
      }

      /**
       * @param {string} name
       * @returns {HTMLElement | null}
       */
      #part(name) {
        return this.querySelector(`[data-rechner="${name}"]`);
      }

      /** Placeholders and input limits come from the range the matrix covers, e.g. "150 – 210". */
      #setBounds() {
        const all = this.#rows;
        const bounds = {
          height: [Math.min(...all.map((r) => r.groesse_min)), Math.max(...all.map((r) => r.groesse_max))],
          weight: [Math.min(...all.map((r) => r.gewicht_axis_min)), Math.max(...all.map((r) => r.gewicht_axis_max))],
        };

        for (const [name, [min, max]] of Object.entries(bounds)) {
          const input = this.#part(name);
          if (!(input instanceof HTMLInputElement)) continue;
          input.min = String(min);
          input.max = String(max);
          input.placeholder = `${min} – ${max}`;
        }
      }

      /** @param {MouseEvent} event */
      #onClick = (event) => {
        const target = event.target instanceof Element ? event.target.closest('[data-rechner]') : null;
        if (!(target instanceof HTMLElement)) return;

        switch (target.dataset.rechner) {
          case 'position':
            this.#selectPosition(target);
            break;
          case 'back':
            this.dataset.view = 'form';
            break;
          case 'apply':
            this.#apply();
            break;
        }
      };

      #onInput = () => this.#setError(false);

      /** @param {Event} event */
      #onSubmit = (event) => {
        event.preventDefault();
        this.#submit();
      };

      /** @param {MouseEvent} event */
      #onDocumentClick = (event) => {
        const link = event.target instanceof Element ? event.target.closest(OPENER_SELECTOR) : null;
        if (!link) return;

        event.preventDefault();
        this.#syncRows();
        this.#dialog('showDialog');
      };

      /**
       * Malte's model switcher (blocks/amq-model-switcher.liquid) swaps the variant picker with the
       * chosen model's one while this dialog stays on the page. The picker fork ships the matrix rows
       * of the product it was rendered for, so the dialog takes those rows whenever it opens and
       * calculates with the active model's matrix. Without such a script the block's own rows stay.
       */
      #syncRows() {
        const script = document.querySelector('variant-picker script[data-amq-haertegrad-rows]');
        if (!(script instanceof HTMLScriptElement)) return;

        const productId = script.dataset.productId ?? '';
        if (productId === this.#rowsProductId) return;

        try {
          const rows = JSON.parse(script.textContent ?? '');
          if (!Array.isArray(rows) || !rows.length) return;

          this.#rows = pickRows(rows);
          this.#rowsProductId = productId;
          this.#setBounds();
          this.#grade = null;
          this.dataset.view = 'form';
          this.dataset.state = 'idle';
          const note = this.#part('unavailable');
          if (note) note.hidden = true;
        } catch (error) {
          console.warn('[amq-haertegrad-rechner] matrix rows in the picker are not valid JSON', error);
        }
      }

      /** @param {'showDialog' | 'closeDialog'} method */
      #dialog(method) {
        const host = /** @type {any} */ (this.closest('dialog-component'));
        if (!host) return;

        customElements
          .whenDefined('dialog-component')
          .then(() => host[method]?.())
          .catch((error) => console.warn('[amq-haertegrad-rechner]', error));
      }

      /** @param {HTMLElement} button */
      #selectPosition(button) {
        for (const other of this.querySelectorAll('[data-rechner="position"]')) {
          other.setAttribute('aria-pressed', String(other === button));
        }
      }

      /** @param {boolean} show */
      #setError(show) {
        const error = this.#part('error');
        if (error) error.hidden = !show;
      }

      #submit() {
        if (this.#timer !== undefined) return; // ignore repeat clicks while the spinner runs

        const height = Number.parseInt(/** @type {HTMLInputElement | null} */ (this.#part('height'))?.value ?? '', 10);
        const weight = Number.parseInt(/** @type {HTMLInputElement | null} */ (this.#part('weight'))?.value ?? '', 10);

        if (Number.isNaN(height) || Number.isNaN(weight)) {
          this.#setError(true);
          return;
        }
        this.#setError(false);

        const submit = this.#part('submit');
        submit?.setAttribute('aria-busy', 'true');
        submit?.setAttribute('disabled', '');

        this.#timer = setTimeout(() => {
          this.#timer = undefined;
          submit?.removeAttribute('aria-busy');
          submit?.removeAttribute('disabled');
          this.#render(calculate(this.#rows, height, weight));
        }, SPINNER_MS);
      }

      /** @param {Recommendation} result */
      #render(result) {
        this.#grade = result.state === 'not-found' ? null : result.grade ?? null;
        this.dataset.state = result.state;

        if (this.#grade) {
          const { name, range } = describe(result, this.#texts);
          const parts = { grade: this.#grade, name, range };
          for (const [part, text] of Object.entries(parts)) {
            const element = this.#part(part);
            if (element) element.textContent = text;
          }
        }

        const note = this.#part('unavailable');
        if (note) note.hidden = true;
        this.dataset.view = 'result';
      }

      /**
       * The picker's radio input for a grade: an option whose name contains "Härtegrad" and whose
       * value carries exactly that one digit ("Härtegrad 3"; combinations like "Härtegrade 2 & 3"
       * are not matched).
       * @param {string} grade
       * @returns {HTMLInputElement | null}
       */
      #pickerInput(grade) {
        const number = grade.replace(/\D/g, '');
        const scope = this.closest('.shopify-section') ?? document;

        for (const input of scope.querySelectorAll('variant-picker fieldset input[type="radio"]')) {
          if (!(input instanceof HTMLInputElement)) continue;
          if (!/h[äa]rtegrad/i.test(input.dataset.optionName ?? '')) continue;

          const digits = input.value.match(/\d/g) ?? [];
          if (digits.length === 1 && digits[0] === number) return input;
        }
        return null;
      }

      /** "Härtegrad auswählen": select the grade in the buy box and close the dialog. */
      #apply() {
        if (!this.#grade) return;

        const input = this.#pickerInput(this.#grade);
        const selectable = Boolean(input) && !input?.disabled && input?.dataset.optionAvailable !== 'false';

        this.dispatchEvent(
          new CustomEvent('amq-haertegrad:apply', { bubbles: true, detail: { grade: this.#grade, applied: selectable } })
        );

        if (input && !selectable) {
          // The grade exists but not in the chosen size: say so instead of selecting a dead value
          const note = this.#part('unavailable');
          if (note) note.hidden = false;
          return;
        }

        if (input && !input.checked) input.click();
        this.#dialog('closeDialog');
      }
    }
  );
}
